"use client";

import { useEffect, useState } from "react";
import swal from "sweetalert";
import { useGrantPortalAccess } from "@/modules/user/infraestructure/services/auth";
import { AdminRoles } from "@/config/adminRoles";
import LineasDeSupervisor from "./LineasDeSupervisor";
import { puedeTenerLineas } from "@/config/permissions";
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	Icon,
	Input,
	Select,
	SelectItem,
} from "@umahealth/occipital/client";
import { Button, Paragraph, Title } from "@umahealth/occipital";
import { IUser, IUserBase } from "@/modules/users/domain/IUser";
import {
	IUserEditableFields,
	updateUser,
} from "@/modules/users/infraestructure/usersRequests";
import {
	dniOptions,
	positionOptions,
	sexOptions,
} from "src/storybook/components/Inputs/Combobox/OperatorCombobox/OperatorCombobox";
import {
	Matricula,
	guardarMatricula,
	traerMatricula,
} from "@/modules/users/infraestructure/matriculaRequests";

const ROL_MEDICO = 4;
const ROL_ENFERMERO = 5;

const tiposDeMatricula = [
	{ value: "MN", label: "Matrícula Nacional (MN)" },
	{ value: "MP", label: "Matrícula Provincial (MP)" },
];

/** `2026-09-05`, que es lo que espera un input date. */
/**
 * Un `Select` con su nombre de campo a la vista.
 *
 * El `Select` de occipital usa `label` sólo como `aria-label`: la leyenda que
 * flota sobre el borde la dibuja con el **placeholder**. O sea que un campo con
 * valor mostraba "Sin definir" arriba y "Trenes" adentro —el rótulo decía lo
 * contrario que el contenido— y un campo sin valor no mostraba rótulo alguno.
 * Con tres selects seguidos así, la única forma de saber qué era cada uno era
 * adivinarlo por el valor.
 *
 * En vez de pelear con el componente, el nombre del campo va arriba como
 * `<label>`, igual que en los filtros del listado, y el placeholder queda para
 * lo que es: decir que todavía no hay nada elegido.
 */
function CampoConEtiqueta({
	etiqueta,
	children,
}: {
	etiqueta: string;
	children: React.ReactNode;
}) {
	return (
		<div className="flex flex-col mt-3">
			<span className="text-xs text-grey-600 mb-1">{etiqueta}</span>
			{children}
		</div>
	);
}

function aFechaInput(valor: Date | string | undefined): string {
	if (!valor) return "";
	const fecha = new Date(valor);
	if (Number.isNaN(fecha.getTime())) return "";
	return fecha.toISOString().slice(0, 10);
}

type Formulario = {
	firstName: string;
	lastName: string;
	nomina: string;
	sex: string;
	dob: string;
	phone: string;
	documentType: string;
	documentNumber: string;
	position: string;
	base: string;
	padron: string;
};

function desdeUsuario(user: IUser): Formulario {
	return {
		firstName: user.firstName ?? "",
		lastName: user.lastName ?? "",
		nomina: user.nomina ?? "",
		sex: user.sex ?? "",
		dob: aFechaInput(user.dob),
		phone: user.phone ?? "",
		documentType: user.documentType ?? "",
		documentNumber: user.documentNumber ?? "",
		position: user.position ?? "",
		base: user.base?.id ? String(user.base.id) : "",
		padron: user.padron ?? "",
	};
}

/**
 * Sólo lo que cambió.
 *
 * La API rechaza un body vacío, y mandar campos sin tocar sería pisar datos
 * con lo que el formulario haya interpretado al cargarlos.
 */
function soloCambios(
	inicial: Formulario,
	actual: Formulario,
): IUserEditableFields {
	const cambios: IUserEditableFields = {};
	(Object.keys(actual) as (keyof Formulario)[]).forEach((campo) => {
		const valor = actual[campo].trim();
		if (valor === inicial[campo].trim()) return;
		if (campo === "base") {
			if (valor) cambios.base = Number(valor);
			return;
		}
		if (campo === "phone") {
			cambios.phone = valor || null;
			return;
		}
		if (campo === "padron") {
			// Estrecha el string del formulario al par que acepta la API, y de
			// paso no manda un valor que no sea uno de los dos.
			if (valor === "trenes" || valor === "emergencias") {
				cambios.padron = valor;
			}
			return;
		}
		if (!valor) return;
		cambios[campo] = valor;
	});
	return cambios;
}

/** Los perfiles de cliente con los que se puede crear el acceso al portal. */
const OPCIONES_DE_ROL_DE_ACCESO = [
	{
		rol: AdminRoles.SUPERVISOR_OPERATIVO as const,
		titulo: "Supervisor operativo",
		detalle: "Jefe de estación: valida las evaluaciones y troqueles de sus líneas.",
	},
	{
		rol: AdminRoles.SUPERVISOR_LINEA as const,
		titulo: "Supervisor de línea",
		detalle:
			"Ve sólo Indicadores por línea. Después de crear el acceso, elegí sus líneas acá mismo.",
	},
];

export default function EditUserDrawer({
	user,
	bases,
	esAdmin,
	puedeEditarMatricula: puedeEditarMatriculaSegunRol,
	puedeDarAcceso,
	onClose,
	onSaved,
}: {
	/** `null` cierra el drawer. */
	user: IUser | null;
	bases: IUserBase[];
	/** Reservado para lo que sea exclusivo de admin. */
	esAdmin?: boolean;
	/**
	 * Admin **u owner**: es lo que acepta `PATCH /api/doctors/:uid/license`.
	 * Antes esto se derivaba de `esAdmin` y un owner no veía el campo aunque
	 * la API se lo hubiera permitido.
	 */
	puedeEditarMatricula?: boolean;
	/**
	 * Crear la cuenta de acceso: admin o soporte, igual que el reseteo de
	 * contraseña. La API valida lo mismo; esto sólo decide qué se muestra.
	 */
	puedeDarAcceso?: boolean;
	onClose: () => void;
	onSaved: () => void;
}) {
	const [form, setForm] = useState<Formulario | null>(null);
	const [inicial, setInicial] = useState<Formulario | null>(null);
	const [guardando, setGuardando] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const esMedico = !!user?.roles?.some((rol) => rol.id === ROL_MEDICO);
	/**
	 * El enfermero también firma el protocolo, así que también lleva matrícula.
	 *
	 * Su bloque del imprimible salía con nombre y firma y nada más, mientras el
	 * del médico llevaba la suya. Es el mismo planteo de legalidad, aplicado al
	 * otro firmante.
	 */
	const esEnfermero = !!user?.roles?.some((rol) => rol.id === ROL_ENFERMERO);
	const puedeEditarMatricula =
		!!puedeEditarMatriculaSegunRol && (esMedico || esEnfermero);

	// La matrícula no está en el usuario de SQL: vive en el perfil profesional,
	// así que se pide aparte al abrir el drawer.
	const [matricula, setMatricula] = useState<Matricula | null>(null);
	const [numero, setNumero] = useState("");
	const [tipo, setTipo] = useState("");
	const [especialidad, setEspecialidad] = useState("");
	const [cargandoMatricula, setCargandoMatricula] = useState(false);

	/**
	 * Acceso al portal.
	 *
	 * Se ofrece sólo si la persona no tiene email, que es como está toda la
	 * nómina de Trenes: sin email no hay cuenta de Firebase, y sin cuenta el
	 * rol de supervisor no le sirve de nada porque no tiene con qué entrar.
	 *
	 * Si ya tiene email, el camino es el reseteo de contraseña del listado, no
	 * este formulario: crear una cuenta que ya existe no es lo que hace falta.
	 */
	const [emailDeAcceso, setEmailDeAcceso] = useState("");
	const [claveDeAcceso, setClaveDeAcceso] = useState("");
	const [claveRepetida, setClaveRepetida] = useState("");
	/**
	 * Con qué rol entra la cuenta nueva. Siempre uno: una cuenta sin rol entra
	 * y no ve nada, que es peor que no tener cuenta.
	 */
	const [rolDeAcceso, setRolDeAcceso] = useState<
		AdminRoles.SUPERVISOR_OPERATIVO | AdminRoles.SUPERVISOR_LINEA
	>(AdminRoles.SUPERVISOR_OPERATIVO);
	const [errorDeAcceso, setErrorDeAcceso] = useState<string | null>(null);
	const darAcceso = useGrantPortalAccess();

	useEffect(() => {
		setEmailDeAcceso("");
		setClaveDeAcceso("");
		setClaveRepetida("");
		setRolDeAcceso(AdminRoles.SUPERVISOR_OPERATIVO);
		setErrorDeAcceso(null);
	}, [user?.uid]);

	useEffect(() => {
		if (!user) {
			setForm(null);
			setInicial(null);
			setError(null);
			return;
		}
		const valores = desdeUsuario(user);
		setForm(valores);
		setInicial(valores);
		setError(null);
	}, [user]);

	useEffect(() => {
		if (!user || !puedeEditarMatricula) {
			setMatricula(null);
			setNumero("");
			setTipo("");
			setEspecialidad("");
			return;
		}

		let vigente = true;
		setCargandoMatricula(true);

		traerMatricula(user.uid)
			.then((datos) => {
				if (!vigente) return;
				setMatricula(datos);
				setNumero(datos.matricula ? String(datos.matricula) : "");
				setTipo(datos.licenseType ?? "");
				setEspecialidad(datos.speciality ?? "");
			})
			.catch(() => {
				if (!vigente) return;
				setMatricula(null);
			})
			.finally(() => vigente && setCargandoMatricula(false));

		return () => {
			vigente = false;
		};
	}, [user, puedeEditarMatricula]);

	if (!user || !form || !inicial) return null;

	const set = (campo: keyof Formulario) => (valor: string) =>
		setForm((previo) => (previo ? { ...previo, [campo]: valor } : previo));

	const cambios = soloCambios(inicial, form);

	const cambioLaMatricula =
		puedeEditarMatricula &&
		!!numero.trim() &&
		(numero.trim() !== String(matricula?.matricula ?? "") ||
			tipo !== (matricula?.licenseType ?? ""));

	const haycambios = Object.keys(cambios).length > 0 || cambioLaMatricula;

	const guardar = async () => {
		if (!haycambios) return;
		setGuardando(true);
		setError(null);
		try {
			if (Object.keys(cambios).length > 0) {
				await updateUser(user.uid, cambios);
			}

			if (cambioLaMatricula) {
				await guardarMatricula(user.uid, {
					matricula: Number(numero.trim()),
					licenseType: tipo,
					speciality: especialidad.trim() || undefined,
				});
			}

			onSaved();
			onClose();
		} catch (err) {
			setError(
				err instanceof Error
					? err.message
					: "No se pudieron guardar los cambios",
			);
		} finally {
			setGuardando(false);
		}
	};

	function enviarAcceso(evento: React.FormEvent) {
		evento.preventDefault();
		setErrorDeAcceso(null);

		if (!user) return;
		if (!emailDeAcceso.trim()) {
			setErrorDeAcceso("Hace falta un email: es con lo que va a entrar.");
			return;
		}
		if (claveDeAcceso.length < 6) {
			setErrorDeAcceso("La contraseña necesita al menos 6 caracteres.");
			return;
		}
		if (claveDeAcceso !== claveRepetida) {
			setErrorDeAcceso("Las contraseñas no coinciden.");
			return;
		}

		darAcceso.mutate(
			{
				uid: user.uid,
				email: emailDeAcceso.trim(),
				password: claveDeAcceso,
				role: rolDeAcceso,
			},
			{
				onSuccess: () => {
					onSaved?.();
					onClose();
					swal(
						"Acceso creado",
						`${user.firstName} ${user.lastName} ya puede entrar al portal con ${emailDeAcceso.trim()}. Pasale la contraseña por un canal seguro; conviene que la cambie en el primer ingreso.`,
						"success",
					);
				},
				onError: (err: unknown) => {
					setErrorDeAcceso(
						err instanceof Error ? err.message : "No se pudo dar el acceso",
					);
				},
			},
		);
	}

	return (
		<Drawer open onOpenChange={(abierto) => !abierto && onClose()}>
			<DrawerContent className="h-[97%] rounded-t-[48px] px-8 sm:px-12">
				<DrawerHeader>
					<div className="flex flex-row w-full items-center justify-between mb-2 mt-6">
						<DrawerTitle asChild>
							<Title
								hierarchy="h2"
								size="text-l"
								color="text-primary-800"
								weight="font-semibold"
								className="m-0"
							>
								Editar datos
							</Title>
						</DrawerTitle>
						<DrawerClose>
							<Icon name="close" size="size-6" color="text-grey-500" />
						</DrawerClose>
					</div>
					<Paragraph className="text-sm text-grey-500 mb-6">
						Se guarda sólo lo que modifiques. El legajo va tal cual figura
						en el padrón, sin ceros adelante.
					</Paragraph>

					<DrawerDescription asChild>
						<div className="overflow-y-auto max-h-[60vh] pb-4 flex flex-col gap-1">
							<Input
								label="Nombre"
								value={form.firstName}
								onChange={(e) => set("firstName")(e.target.value)}
							/>
							<Input
								label="Apellido"
								value={form.lastName}
								onChange={(e) => set("lastName")(e.target.value)}
							/>
							<Input
								label="Legajo"
								inputMode="numeric"
								value={form.nomina}
								onChange={(e) => set("nomina")(e.target.value)}
							/>

							{/*
							  * Matrícula y acceso van arriba, no al final.
							  *
							  * Estaban después de Base, o sea a cinco campos de scroll dentro
							  * de un panel de 60vh: la matrícula del enfermero parecía no
							  * existir incluso para quien sabía que estaba. Son las dos cosas
							  * que un admin viene a hacer puntualmente; el resto son datos que
							  * llegan del padrón y casi no se editan.
							  */}
			{/*
			  * La matrícula va como dos campos más, sin encabezado ni explicación.
			  *
			  * Tenía un título y un párrafo de dos renglones, y con eso pesaba más
			  * que cualquier otro dato del panel: parecía una sección aparte
			  * cuando es un campo que se llena y listo. Las etiquetas de los
			  * inputs ya dicen qué son.
			  */}
							{puedeEditarMatricula && (
								<div className="flex flex-col gap-1">
									{cargandoMatricula ? (
										<Paragraph className="text-sm text-grey-500">
											Buscando la matrícula...
										</Paragraph>
									) : (
										<>
											{esMedico && matricula && !matricula.tieneProvider && (
												<div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mb-3">
													<span className="text-sm text-yellow-800">
														Este médico no tiene perfil profesional, así
														que hoy no puede entrar al portal médico.
														Cargale la matrícula y la especialidad para
														crearlo.
													</span>
												</div>
											)}

											<Input
												label="Número de matrícula"
												inputMode="numeric"
												value={numero}
												onChange={(e) => setNumero(e.target.value)}
											/>
											{/* Con etiqueta arriba, como los demás selects: occipital
											    usa el placeholder como leyenda flotante y este quedaba
											    rotulado "Sin definir". */}
											<CampoConEtiqueta etiqueta="Tipo de matrícula">
											<Select
												label="Tipo de matrícula"
												contentClassName="z-[60]"
												placeholder="Sin definir"
												value={tipo || undefined}
												onValueChange={setTipo}
											>
												{tiposDeMatricula.map(({ label, value }) => (
													<SelectItem value={value} key={value}>
														{label}
													</SelectItem>
												))}
											</Select>
											</CampoConEtiqueta>

											{esMedico && matricula && !matricula.tieneProvider && (
												<Input
													label="Especialidad"
													value={especialidad}
													onChange={(e) => setEspecialidad(e.target.value)}
												/>
											)}
										</>
									)}
								</div>
							)}

							{/* Las líneas del supervisor de línea o del supervisor
							    operativo (US 27422): quien administra roles. */}
							{esAdmin && puedeTenerLineas(user?.roles) && (
								<LineasDeSupervisor user={user} bases={bases} onSaved={onSaved} />
							)}

							{puedeDarAcceso && !user?.email && (
								<div className="mt-8 pt-6 border-t border-grey-200 flex flex-col gap-1">
									<Title
										hierarchy="h3"
										size="text-m"
										color="text-primary-800"
										weight="font-semibold"
										className="m-0"
									>
										Acceso al portal
									</Title>
									<Paragraph className="text-sm text-grey-500 mb-2">
										Esta persona está en la nómina y hoy no puede entrar al
										portal: no tiene cuenta. Creásela si además va a ser
										supervisor de base o de línea. Sigue siendo evaluable como
										siempre.
									</Paragraph>

									<form onSubmit={enviarAcceso} className="flex flex-col gap-3">
										<Input
											label="Email con el que va a entrar"
											type="email"
											value={emailDeAcceso}
											onChange={(e) => setEmailDeAcceso(e.target.value)}
										/>
										<Input
											label="Contraseña inicial"
											type="password"
											value={claveDeAcceso}
											onChange={(e) => setClaveDeAcceso(e.target.value)}
										/>
										<Input
											label="Repetir la contraseña"
											type="password"
											value={claveRepetida}
											onChange={(e) => setClaveRepetida(e.target.value)}
										/>

										<fieldset className="flex flex-col gap-1.5">
											<legend className="text-sm font-medium text-grey-700 mb-1">
												Con qué rol entra
											</legend>
											{OPCIONES_DE_ROL_DE_ACCESO.map((opcion) => (
												<label
													key={opcion.rol}
													className="flex items-start gap-2 text-sm text-grey-700 cursor-pointer"
												>
													<input
														type="radio"
														name="rol-de-acceso"
														checked={rolDeAcceso === opcion.rol}
														onChange={() => setRolDeAcceso(opcion.rol)}
														className="accent-primary-700 w-4 h-4 mt-0.5"
													/>
													<span>
														<span className="font-medium">{opcion.titulo}.</span>{" "}
														{opcion.detalle}
													</span>
												</label>
											))}
										</fieldset>

										{errorDeAcceso && (
											<div className="bg-red-50 border border-red-300 rounded-lg p-3">
												<span className="text-sm text-error-700">
													{errorDeAcceso}
												</span>
											</div>
										)}

										<Button
											type="submit"
											loading={darAcceso.isPending}
											disabled={darAcceso.isPending}
											className="w-full bg-primary-700 text-white rounded disabled:opacity-50"
										>
											{darAcceso.isPending ? "Creando..." : "Crear el acceso"}
										</Button>
									</form>
								</div>
							)}

							<CampoConEtiqueta etiqueta="Tipo de documento">
								<Select
									label="Tipo de documento"
									contentClassName="z-[60]"
									placeholder="Sin definir"
									value={form.documentType || undefined}
									onValueChange={set("documentType")}
								>
									{dniOptions.map(({ label, value }) => (
										<SelectItem value={value} key={value}>
											{label}
										</SelectItem>
									))}
								</Select>
							</CampoConEtiqueta>
							<Input
								label="Número de documento"
								inputMode="numeric"
								value={form.documentNumber}
								onChange={(e) => set("documentNumber")(e.target.value)}
							/>

							<Input
								label="Fecha de nacimiento"
								type="date"
								value={form.dob}
								onChange={(e) => set("dob")(e.target.value)}
							/>
							<CampoConEtiqueta etiqueta="Sexo">
								<Select
									label="Sexo"
									contentClassName="z-[60]"
									placeholder="Sin definir"
									value={form.sex || undefined}
									onValueChange={set("sex")}
								>
									{sexOptions.map(({ label, value }) => (
										<SelectItem value={value} key={value}>
											{label}
										</SelectItem>
									))}
								</Select>
							</CampoConEtiqueta>
							<CampoConEtiqueta etiqueta="Padrón">
								<Select
									label="Padrón"
									contentClassName="z-[60]"
									placeholder="Sin definir"
									value={form.padron || undefined}
									onValueChange={set("padron")}
								>
									<SelectItem value="trenes">Trenes</SelectItem>
									<SelectItem value="emergencias">Emergencias</SelectItem>
								</Select>
							</CampoConEtiqueta>
							<Input
								label="Teléfono"
								value={form.phone}
								onChange={(e) => set("phone")(e.target.value)}
							/>

							<CampoConEtiqueta etiqueta="Puesto de trabajo">
								<Select
									label="Puesto de trabajo"
									contentClassName="z-[60] max-h-[300px]"
									placeholder="Sin definir"
									value={form.position || undefined}
									onValueChange={set("position")}
								>
									{positionOptions.map(({ label, value }) => (
										<SelectItem value={value} key={value}>
											{label}
										</SelectItem>
									))}
								</Select>
							</CampoConEtiqueta>
							<CampoConEtiqueta etiqueta="Base">
								<Select
									label="Base"
									contentClassName="z-[60] max-h-[300px]"
									placeholder="Sin definir"
									value={form.base || undefined}
									onValueChange={set("base")}
								>
									{bases.map((base) => (
										<SelectItem value={String(base.id)} key={base.id}>
											{base.name}
										</SelectItem>
									))}
								</Select>
							</CampoConEtiqueta>

						</div>
					</DrawerDescription>
				</DrawerHeader>

				<DrawerFooter className="sticky bottom-0 w-full">
					{error && (
						<div className="w-full bg-red-50 border-2 border-red-400 rounded-lg p-3 mb-2 flex items-start gap-2">
							<Icon
								name="warning"
								size="size-6"
								color="text-error-600"
								className="flex-shrink-0"
							/>
							<span className="text-error-700 text-sm">{error}</span>
						</div>
					)}
					<Button
						type="button"
						onClick={guardar}
						loading={guardando}
						disabled={guardando || !haycambios}
						className="w-full p-2 bg-primary-700 text-white rounded mb-8 disabled:opacity-50"
					>
						{guardando
							? "Guardando..."
							: haycambios
								? "Guardar cambios"
								: "Sin cambios"}
					</Button>
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	);
}
